import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  InstagramSVG, TikTokSVG, YouTubeSVG, TwitchSVG, TelegramSVG,
  TwitterSVG, FacebookSVG, DiscordSVG, LinkedInSVG, SnapchatSVG,
  SpotifySVG, KickSVG,
} from "../components/PlatformIcons";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useCart } from "../contexts/CartContext";
import { t, L } from "../lib/translations";
import { toast } from "sonner";
import {
  Heart, Users, ShoppingCart, Check, Minus, Plus,
  Zap, Package, Eye, MessageSquare, ArrowRight, Coins,
  Link as LinkIcon, ChevronLeft, ThumbsUp, Radio, Gift, X,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import {
  canonicalizeInstagramPostUrl,
  canonicalizeInstagramProfileUrl,
} from "../lib/instagram";
import {
  canonicalizeTikTokProfileUrl,
  canonicalizeTikTokVideoUrl,
} from "../lib/tiktok";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Platform SVG Icons (imported from shared component) ───────────────────────

// ── Config ───────────────────────────────────────────────────────────────────
const PLATFORM_CONFIG = {
  instagram: { key:"instagram", label:"Instagram", icon:InstagramSVG, color:"#E1306C" },
  tiktok:    { key:"tiktok",    label:"TikTok",    icon:TikTokSVG,    color:"#EE1D52" },
  youtube:   { key:"youtube",   label:"YouTube",   icon:YouTubeSVG,   color:"#FF0000" },
  twitch:    { key:"twitch",    label:"Twitch",    icon:TwitchSVG,    color:"#9146FF" },
  telegram:  { key:"telegram",  label:"Telegram",  icon:TelegramSVG,  color:"#2AABEE" },
  twitter:   { key:"twitter",   label:"X",         icon:TwitterSVG,   color:"#1A8CD8" },
  facebook:  { key:"facebook",  label:"Facebook",  icon:FacebookSVG,  color:"#1877F2" },
  discord:   { key:"discord",   label:"Discord",   icon:DiscordSVG,   color:"#5865F2" },
  linkedin:  { key:"linkedin",  label:"LinkedIn",  icon:LinkedInSVG,  color:"#0A66C2" },
  kick:      { key:"kick",      label:"Kick",      icon:KickSVG,      color:"#53FC18" },
  snapchat:  { key:"snapchat",  label:"Snapchat",  icon:SnapchatSVG,  color:"#FFFC00" },
  spotify:   { key:"spotify",   label:"Spotify",   icon:SpotifySVG,   color:"#1DB954" },
};
const PLATFORM_ORDER = [
  "instagram","tiktok","telegram","twitch","facebook","twitter","linkedin","kick",
  "youtube","discord","snapchat","spotify",
];

const CATEGORY_CONFIG = {
  followers:   { icon:Users,         color:"#8B5CF6" },
  likes:       { icon:Heart,         color:"#E1306C" },
  views:       { icon:Eye,           color:"#0EA5E9" },
  comments:    { icon:MessageSquare, color:"#F59E0B" },
  story_views: { icon:Eye,           color:"#8B5CF6" },
  story_likes: { icon:Heart,         color:"#EC4899" },
  page_likes:  { icon:Users,         color:"#2563EB" },
  reactions:   { icon:ThumbsUp,      color:"#F59E0B" },
  live_views:  { icon:Radio,         color:"#EF4444" },
};
const DEFAULT_CONFIG = CATEGORY_CONFIG.followers;
const CATEGORY_ORDER = ["followers","likes","views","comments","story_views","story_likes","page_likes","reactions","live_views"];
const prefersReducedMotion = typeof window !== "undefined"
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Inject reduced-motion CSS overrides once so CSS animation classes respect the preference
if (typeof document !== "undefined" && prefersReducedMotion) {
  const style = document.createElement("style");
  style.textContent = `
    .badge-pulse, .cat-card-enter, .product-card-enter, .popular-card-enter, .pulse-dot {
      animation: none !important;
      transition: none !important;
    }
  `;
  document.head.appendChild(style);
}

const getProductPlatform = (p) => {
  const plat = String(p?.platform || "").toLowerCase();
  return (!plat || plat === "none") ? "__none__" : plat;
};
const getTargetMode = (product) => {
  const explicit = String(product?.target_mode || "").toLowerCase();
  if (explicit) return explicit;
  if (product?.category === "followers") return "instagram_profile";
  if (product?.category === "likes")     return "instagram_post";
  return "generic";
};
const getCategoryLabel = (category, lang) => {
  switch (String(category || "").toLowerCase()) {
    case "followers":   return t("shop.followers", lang);
    case "likes":       return t("shop.likes", lang);
    case "views":       return t("Shop.views", lang);
    case "comments":    return t("Shop.comments", lang);
    case "story_views": return t("Shop.story_views", lang);
    case "story_likes": return t("Shop.story_likes", lang);
    case "page_likes":  return t("Shop.page_likes", lang);
    case "reactions":   return t("Shop.reactions", lang);
    case "live_views":  return t("Shop.live_views", lang);
    default:            return category || t("nav.shop", lang);
  }
};
const ensureHttps = (url) => {
  if (url && !/^https?:\/\//i.test(url)) return "https://" + url;
  return url;
};
const normalizeTargetInput = (rawValue, product) => {
  const raw = String(rawValue || "").trim();
  const mode = getTargetMode(product);
  if (!raw) return "";
  if (mode === "instagram_profile") return canonicalizeInstagramProfileUrl(raw);
  if (mode === "instagram_post")    return canonicalizeInstagramPostUrl(raw);
  if (mode === "tiktok_profile")    return canonicalizeTikTokProfileUrl(raw);
  if (mode === "tiktok_video")      return canonicalizeTikTokVideoUrl(raw);
  return ensureHttps(raw);
};
const validateTargetInput = (rawValue, product, lang) => {
  const raw = String(rawValue || "").trim();
  const mode = getTargetMode(product);
  if (!raw) return t("Shop.enter_the_required_link", lang);
  const normalized = normalizeTargetInput(raw, product);
  const value = normalized || raw;
  // Use product's link_regex if set
  if (product?.link_regex) {
    try { if (!new RegExp(product.link_regex).test(value)) return t("Shop.enter_a_valid_link", lang); }
    catch {}
    return null;
  }
  // Built-in per-mode fallback
  if (mode === "instagram_profile" && !normalized) return t("Shop.enter_a_valid_instagram_profile_link", lang);
  if (mode === "instagram_post"    && !normalized) return t("Shop.enter_a_valid_instagram_post_link", lang);
  if (mode === "tiktok_profile"    && !normalized) return L(lang, "Enter a valid TikTok profile link", "Введите корректную ссылку на профиль TikTok", "הזן קישור תקין לפרופיל TikTok");
  if (mode === "tiktok_video"      && !normalized) return L(lang, "Enter a valid TikTok video link", "Введите корректную ссылку на видео TikTok", "הזן קישור תקין לסרטון TikTok");
  if (mode === "instagram_story") {
    if (!/instagram\.com\/stories\/[A-Za-z0-9_.]+\/[0-9]+/.test(value))
      return L(lang, "Paste the full story link: instagram.com/stories/nickname/STORY_ID/",
                     "Вставьте полную ссылку на сторис: instagram.com/stories/nickname/STORY_ID/",
                     "הדבק את הקישור המלא לסטורי: instagram.com/stories/nickname/STORY_ID/");
  }
  return null;
};
const getTargetFieldMeta = (product, lang) => {
  const mode = getTargetMode(product);
  switch (mode) {
    case "instagram_profile": return {
      label: t("Shop.profile_link", lang), placeholder: "instagram.com/nickname",
      hint: t("Shop.paste_your_instagram_profile_link", lang),
      examples: ["https://instagram.com/nickname", "https://www.instagram.com/nickname"],
    };
    case "instagram_post": return {
      label: t("Shop.post_reel_link", lang), placeholder: "instagram.com/p/POST_ID/",
      hint: t("Shop.paste_your_instagram_post_or_reel_link", lang),
      examples: ["https://www.instagram.com/p/POST_ID/", "https://www.instagram.com/reel/REEL_ID/", "https://www.instagram.com/tv/VIDEO_ID/"],
    };
    case "instagram_story": return {
      label: t("Shop.story_link", lang), placeholder: "instagram.com/stories/nickname/3882953131684581738/",
      hint: t("Shop.paste_your_instagram_story_link", lang),
      examples: ["https://www.instagram.com/stories/nickname/3882953131684581738/"],
    };
    case "facebook_post": return {
      label: t("Shop.facebook_post_link", lang), placeholder: "facebook.com/nickname/posts/POST_ID",
      hint: t("Shop.paste_your_facebook_post_link", lang),
      examples: ["https://www.facebook.com/nickname/posts/POST_ID", "https://www.facebook.com/photo?fbid=POST_ID"],
    };
    case "facebook_page": return {
      label: t("Shop.facebook_page_link", lang), placeholder: "facebook.com/nickname",
      hint: t("Shop.paste_your_facebook_page_link", lang),
      examples: ["https://facebook.com/nickname", "https://www.facebook.com/nickname"],
    };
    case "twitter_profile": return {
      label: t("Shop.x_twitter_profile", lang), placeholder: "x.com/nickname",
      hint: t("Shop.paste_your_x_twitter_profile_link", lang),
      examples: ["https://x.com/nickname", "https://twitter.com/nickname", "@nickname"],
    };
    case "twitter_post": return {
      label: t("Shop.x_twitter_post", lang), placeholder: "x.com/nickname/status/POST_ID",
      hint: t("Shop.paste_your_x_twitter_post_link", lang),
      examples: ["https://x.com/nickname/status/POST_ID", "https://twitter.com/nickname/status/POST_ID"],
    };
    case "tiktok_profile": return {
      label: t("Shop.tiktok_profile", lang), placeholder: "tiktok.com/@nickname",
      hint: t("Shop.paste_your_tiktok_profile_link", lang),
      examples: ["https://www.tiktok.com/@nickname", "https://tiktok.com/@nickname"],
    };
    case "tiktok_video": return {
      label: t("Shop.tiktok_video_link", lang), placeholder: "tiktok.com/@nickname/video/VIDEO_ID",
      hint: t("Shop.paste_your_tiktok_video_link", lang),
      examples: ["https://www.tiktok.com/@nickname/video/VIDEO_ID", "https://vm.tiktok.com/SHORT_ID/"],
    };
    case "youtube_channel": return {
      label: L(lang, "YouTube Channel", "YouTube-канал", "ערוץ YouTube"), placeholder: "youtube.com/@nickname",
      hint: L(lang, "Paste your YouTube channel link", "Вставьте ссылку на YouTube-канал", "הדבק את הקישור לערוץ YouTube"),
      examples: ["https://www.youtube.com/@nickname", "https://youtube.com/channel/CHANNEL_ID"],
    };
    case "youtube_video": return {
      label: L(lang, "YouTube Video", "YouTube-видео", "סרטון YouTube"), placeholder: "youtube.com/watch?v=VIDEO_ID",
      hint: L(lang, "Paste your YouTube video link", "Вставьте ссылку на YouTube-видео", "הדבק את הקישור לסרטון YouTube"),
      examples: ["https://www.youtube.com/watch?v=VIDEO_ID", "https://youtu.be/VIDEO_ID"],
    };
    case "linkedin_profile": return {
      label: t("Shop.linkedin_profile_link", lang), placeholder: "linkedin.com/in/nickname",
      hint: t("Shop.paste_your_linkedin_profile_link", lang),
      examples: ["https://linkedin.com/in/nickname", "https://www.linkedin.com/in/nickname"],
    };
    case "linkedin_post": return {
      label: t("Shop.linkedin_post_link", lang), placeholder: "linkedin.com/posts/nickname_POST_ID",
      hint: t("Shop.paste_your_linkedin_post_link", lang),
      examples: ["https://www.linkedin.com/posts/nickname_POST_ID", "https://www.linkedin.com/feed/update/urn:li:activity:POST_ID"],
    };
    case "telegram_channel": return {
      label: t("Shop.telegram_channel_link", lang), placeholder: "t.me/nickname",
      hint: t("Shop.paste_your_telegram_channel_link", lang),
      examples: ["https://t.me/nickname"],
    };
    case "telegram_post": return {
      label: t("Shop.telegram_post_link", lang), placeholder: "t.me/nickname/POST_ID",
      hint: t("Shop.paste_the_telegram_post_link", lang),
      examples: ["https://t.me/nickname/POST_ID"],
    };
    case "twitch_channel": return {
      label: t("Shop.twitch_channel_link", lang), placeholder: "twitch.tv/nickname",
      hint: t("Shop.paste_your_twitch_channel_link", lang),
      examples: ["https://www.twitch.tv/nickname", "https://twitch.tv/nickname"],
    };
    case "kick_channel": return {
      label: t("Shop.kick_channel_link", lang), placeholder: "kick.com/nickname",
      hint: t("Shop.paste_your_kick_channel_link", lang),
      examples: ["https://kick.com/nickname", "https://www.kick.com/nickname"],
    };
    case "spotify_artist": return {
      label: L(lang, "Spotify Artist", "Spotify-исполнитель", "אמן Spotify"), placeholder: "open.spotify.com/artist/ARTIST_ID",
      hint: L(lang, "Paste your Spotify artist link", "Вставьте ссылку на Spotify-исполнителя", "הדבק את הקישור לאמן Spotify"),
      examples: ["https://open.spotify.com/artist/ARTIST_ID"],
    };
    case "spotify_track": return {
      label: L(lang, "Spotify Track", "Spotify-трек", "שיר Spotify"), placeholder: "open.spotify.com/track/TRACK_ID",
      hint: L(lang, "Paste your Spotify track link", "Вставьте ссылку на Spotify-трек", "הדבק את הקישור לשיר Spotify"),
      examples: ["https://open.spotify.com/track/TRACK_ID"],
    };
    case "discord_server": return {
      label: L(lang, "Discord Server", "Discord-сервер", "שרת Discord"), placeholder: "discord.gg/INVITE_CODE",
      hint: L(lang, "Paste your Discord invite link", "Вставьте ссылку-приглашение Discord", "הדבק קישור הזמנה ל-Discord"),
      examples: ["https://discord.gg/INVITE_CODE"],
    };
    case "snapchat_profile": return {
      label: L(lang, "Snapchat Profile", "Snapchat-профиль", "פרופיל Snapchat"), placeholder: "snapchat.com/add/nickname",
      hint: L(lang, "Paste your Snapchat profile link", "Вставьте ссылку на Snapchat-профиль", "הדבק את הקישור לפרופיל Snapchat"),
      examples: ["https://snapchat.com/add/nickname", "https://www.snapchat.com/add/nickname"],
    };
    default: return {
      label: t("Shop.link", lang), placeholder: "https://...",
      hint: t("Shop.paste_the_required_link", lang),
      examples: [],
    };
  }
};
const clampQuantity = (value, min, max) => {
  const n = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
};
const getQuantityStep = (product) => {
  const min = product.min_quantity || 100;
  if (min <= 10) return 10;
  if (min <= 50) return 50;
  return 100;
};
const calculateIpointsPrice = (quantity, ipointsPer1000) => {
  const unitPrice = Number(ipointsPer1000 || 0);
  if (quantity <= 0 || unitPrice <= 0) return 0;
  const raw = (quantity / 1000) * unitPrice;
  return Math.max(0.01, Math.round(raw * 100) / 100);
};
const fmtPrice = (n) => {
  const v = Number(n) || 0;
  if (Number.isInteger(v)) return v.toLocaleString();
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ── Main Shop Component ──────────────────────────────────────────────────────
export default function Shop() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { lang } = useLanguage();
  const { addToCart } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [platform, setPlatform] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [giftStatus, setGiftStatus] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(0);

  useEffect(() => {
    fetch(`${API}/products`).then(r => r.json())
      .then(prods => setProducts(prods))
      .catch(console.error)
      .finally(() => setLoading(false));
    fetch(`${API}/site/settings-public`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.ipoint_exchange_rate > 0) setExchangeRate(d.ipoint_exchange_rate); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/free-gift/status`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => setGiftStatus(d))
      .catch(() => {});
  }, [user]);

  const validProducts = products.filter(p => Boolean(p?.product_id) && String(p?.category || "").toLowerCase() !== "bundle");

  const activePlatformKeys = PLATFORM_ORDER.filter(key =>
    validProducts.some(p => getProductPlatform(p) === key) ||
    (key === "instagram" && validProducts.some(p => getProductPlatform(p) === "__none__"))
  );

  useEffect(() => {
    if (loading) return;
    const p = searchParams.get("platform");
    const c = searchParams.get("category");
    if (p && activePlatformKeys.includes(p)) {
      setPlatform(p);
      if (c) setCategory(c);
    }
  }, [loading]); // eslint-disable-line

  const handleSelectPlatform = (key) => {
    setPlatform(key);
    setSearchParams({ platform: key });
  };

  const handleSelectCategory = (key) => {
    setCategory(key);
    setSearchParams(prev => ({ ...Object.fromEntries(prev), category: key }));
  };

  const platformFiltered = platform ? validProducts.filter(p => {
    const plat = getProductPlatform(p);
    if (plat === platform) return true;
    if (plat === "__none__" && platform === "instagram") return true;
    return false;
  }) : [];

  const categorySummary = platformFiltered.reduce((acc, p) => {
    const key = String(p.category || "other").toLowerCase();
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const categoryTabs = CATEGORY_ORDER
    .filter(key => categorySummary[key])
    .map(key => ({ key, label: getCategoryLabel(key, lang), count: categorySummary[key] }));

  useEffect(() => {
    if (platform && categoryTabs.length === 1 && !category) {
      setCategory(categoryTabs[0].key);
    }
  }, [platform, categoryTabs.length]); // eslint-disable-line

  useEffect(() => { setCategory(null); }, [platform]);

  const currentCategoryExists = !category || categoryTabs.some(t => t.key === category);
  const filtered = !category || !currentCategoryExists
    ? platformFiltered
    : platformFiltered.filter(p => String(p.category || "").toLowerCase() === category);

  useEffect(() => { if (!currentCategoryExists) setCategory(null); }, [currentCategoryExists]);

  const hasUnclaimed = giftStatus && (!giftStatus.followers_claimed || !giftStatus.likes_claimed);

  return (
    <div className="min-h-screen pt-14 pb-20" data-testid="shop-page">
      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-black font-outfit" style={{ color:"var(--text-primary)" }}>
            {t("Shop.one_platform_all_networks", lang)}
          </h1>
          <p className="text-sm mt-1" style={{ color:"var(--text-secondary)" }}>
            {t("Shop.choose_a_platform_to_see_services", lang)}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin h-7 w-7 border-2 rounded-full" style={{ borderColor:"#8B5CF6", borderTopColor:"transparent" }} />
          </div>
        ) : (
          <>
            {/* ── Platform grid ── */}
            {!platform && (
              <>
                <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "var(--text-secondary)", opacity: 0.5 }}>
                  {L(lang, "Choose a platform", "Выберите платформу", "בחרו פלטפורמה")}
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-3">
                  {PLATFORM_ORDER.filter(key => activePlatformKeys.includes(key)).map(key => {
                    const cfg = PLATFORM_CONFIG[key];
                    if (!cfg) return null;
                    const Icon = cfg.icon;
                    const isActive = true;
                    return (
                      <button key={key}
                        onClick={() => isActive && handleSelectPlatform(key)}
                        data-testid={`platform-${key}`}
                        disabled={!isActive}
                        className="relative flex flex-col justify-between overflow-hidden aspect-square rounded-2xl transition-all"
                        style={{
                          background: "var(--surface-1)",
                          border: `1px solid ${isActive ? `${cfg.color}35` : "var(--border-color)"}`,
                          boxShadow: "none",
                          opacity: isActive ? 1 : 0.28,
                          cursor: isActive ? "pointer" : "default",
                          padding: "11px 11px 10px",
                          transform: "translateZ(0)",
                          transition: prefersReducedMotion ? "none" : "transform 0.15s ease-out, background 0.12s ease-out, border-color 0.12s ease-out, box-shadow 0.15s ease-out",
                        }}
                        onMouseEnter={e => {
                          if (isActive && !prefersReducedMotion) {
                            e.currentTarget.style.background = `${cfg.color}12`;
                            e.currentTarget.style.borderColor = `${cfg.color}60`;
                            e.currentTarget.style.boxShadow = `0 4px 18px ${cfg.color}22`;
                            e.currentTarget.style.transform = "translateY(-2px) translateZ(0)";
                          }
                        }}
                        onMouseLeave={e => {
                          if (isActive) {
                            e.currentTarget.style.background = "var(--surface-1)";
                            e.currentTarget.style.borderColor = `${cfg.color}35`;
                            e.currentTarget.style.boxShadow = "none";
                          }
                          e.currentTarget.style.transform = "translateZ(0)";
                        }}
                      >
                        {/* Top: icon */}
                        <div className="relative z-10"
                          style={{ color: isActive ? cfg.color : "var(--text-secondary)" }}>
                          <Icon size={28} />
                        </div>

                        {/* Bottom: label */}
                        <span className="relative z-10 text-xs font-bold leading-tight text-start"
                          style={{ color: isActive ? "var(--text-primary)" : "var(--text-secondary)" }}>
                          {cfg.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Coming soon footnote */}
                {PLATFORM_ORDER.some(key => !activePlatformKeys.includes(key)) && (
                  <p className="text-[11px] mb-5" style={{ color: "var(--text-secondary)", opacity: 0.4 }}>
                    {L(lang,
                      `+ ${PLATFORM_ORDER.filter(k => !activePlatformKeys.includes(k)).length} more platforms coming soon`,
                      `+ ${PLATFORM_ORDER.filter(k => !activePlatformKeys.includes(k)).length} платформ скоро`,
                      `+ ${PLATFORM_ORDER.filter(k => !activePlatformKeys.includes(k)).length} פלטפורמות נוספות בקרוב`
                    )}
                  </p>
                )}

                {/* Popular Showcase — discovery element below the primary platform selector */}
                <PopularShowcase products={validProducts} lang={lang} user={user} login={login} addToCart={addToCart} exchangeRate={exchangeRate} />

                {/* Gift banner */}
                {hasUnclaimed && (
                  <div className="relative overflow-hidden rounded-2xl p-4 mb-3"
                    style={{ background:"linear-gradient(135deg,#8B5CF6 0%,#4F46E5 100%)", boxShadow:"0 8px 24px rgba(139,92,246,0.45)" }}>
                    <div className="absolute -end-4 -bottom-4 opacity-[0.15] pointer-events-none" style={{ color:"#fff" }}>
                      <Gift style={{ width:80, height:80 }} />
                    </div>
                    <div className="relative z-10 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background:"rgba(255,255,255,0.2)" }}>
                        <Gift className="w-5 h-5" style={{ color:"#fff" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold mb-0.5" style={{ color:"#fff" }}>{t("Shop.free_gift_available", lang)}</div>
                        <div className="text-xs" style={{ color:"rgba(255,255,255,0.75)" }}>{t("Shop.claim_your_free_followers_or_likes", lang)}</div>
                      </div>
                      <button onClick={() => navigate("/dashboard")}
                        className="h-8 px-3 rounded-xl text-xs font-semibold flex-shrink-0 transition-opacity hover:opacity-90"
                        style={{ background:"rgba(255,255,255,0.95)", color:"#8B5CF6" }}>
                        {t("Shop.claim", lang)}
                      </button>
                    </div>
                  </div>
                )}

                {/* Tasks banner */}
                <div className="relative overflow-hidden rounded-2xl p-4 mb-6"
                  style={{ background:"linear-gradient(135deg,#F77737 0%,#E1306C 100%)", boxShadow:"0 8px 24px rgba(247,119,55,0.45)" }}>
                  <div className="absolute -end-4 -bottom-4 opacity-[0.15] pointer-events-none" style={{ color:"#fff" }}>
                    <Zap style={{ width:80, height:80 }} />
                  </div>
                  <div className="relative z-10 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background:"rgba(255,255,255,0.2)" }}>
                      <Zap className="w-5 h-5" style={{ color:"#fff" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold mb-0.5" style={{ color:"#fff" }}>{t("Shop.complete_tasks_earn_ipoints", lang)}</div>
                      <div className="text-xs" style={{ color:"rgba(255,255,255,0.75)" }}>{t("Shop.follow_accounts_like_posts_and_earn_points_for_f", lang)}</div>
                    </div>
                    <button onClick={() => navigate("/dashboard")}
                      className="h-8 px-3 rounded-xl text-xs font-semibold flex-shrink-0 transition-opacity hover:opacity-90"
                      style={{ background:"rgba(255,255,255,0.95)", color:"#F77737" }}>
                      {t("nav.tasks", lang)}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Back to platforms */}
            {platform && (
              <div className="flex items-center gap-3 mb-5">
                <button onClick={() => { setPlatform(null); setCategory(null); setSearchParams({}); }}
                  className="flex items-center gap-1.5 h-8 px-3 rounded-xl text-xs font-semibold transition-all hover:opacity-70"
                  style={{ background:"var(--surface-2)", color:"var(--text-secondary)" }}>
                  <ChevronLeft className="w-3.5 h-3.5 rtl-flip" />
                  {t("Shop.all_platforms", lang)}
                </button>
                {(() => {
                  const cfg = PLATFORM_CONFIG[platform];
                  const Icon = cfg?.icon;
                  return cfg ? (
                    <div className="flex items-center gap-1.5">
                      <div style={{ color: cfg.color }}><Icon size={16} /></div>
                      <span className="text-sm font-bold" style={{ color:"var(--text-primary)" }}>{cfg.label}</span>
                    </div>
                  ) : null;
                })()}
              </div>
            )}

            {/* Category grid */}
            {platform && !category && categoryTabs.length > 1 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {categoryTabs.map((tab, idx) => {
                  const catCfg = CATEGORY_CONFIG[tab.key] || DEFAULT_CONFIG;
                  const CatIcon = catCfg.icon;
                  return (
                    <button key={tab.key} onClick={() => setCategory(tab.key)}
                      className="cat-card-enter relative flex flex-col justify-between overflow-hidden rounded-2xl transition-all"
                      style={{
                        background:`linear-gradient(135deg,${catCfg.color} 0%,${catCfg.color}BB 100%)`,
                        border:`1px solid ${catCfg.color}30`,
                        boxShadow:`0 8px 24px ${catCfg.color}50`,
                        padding:"14px",
                        minHeight:110,
                        animationDelay:`${idx*60}ms`,
                        cursor:"pointer",
                        transform:"translateZ(0)",
                        transition: prefersReducedMotion ? "none" : "transform 0.15s, box-shadow 0.15s",
                      }}
                      onMouseEnter={e => { if (!prefersReducedMotion) e.currentTarget.style.transform="translateY(-3px) translateZ(0)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform="translateZ(0)"; }}
                    >
                      <div className="absolute -bottom-3 -end-3 opacity-[0.18] pointer-events-none select-none" style={{ color:"#fff" }}>
                        <CatIcon style={{ width:72, height:72 }} />
                      </div>
                      <div className="relative z-10" style={{ color:"rgba(255,255,255,0.95)" }}>
                        <CatIcon style={{ width:26, height:26 }} />
                      </div>
                      <div className="relative z-10">
                        <div className="text-sm font-black" style={{ color:"#fff" }}>{tab.label}</div>
                        <div className="text-[11px] font-semibold mt-0.5" style={{ color:"rgba(255,255,255,0.7)" }}>
                          {tab.count} {t("Shop.services", lang)}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Products */}
            {platform && category && (
              <>
                {categoryTabs.length > 1 && (
                  <div className="flex gap-2 flex-wrap mb-6">
                    {categoryTabs.map(tab => {
                      const catCfg = CATEGORY_CONFIG[tab.key] || DEFAULT_CONFIG;
                      const CatIcon = catCfg.icon;
                      const active = category === tab.key;
                      return (
                        <button key={tab.key} type="button" onClick={() => handleSelectCategory(tab.key)}
                          className="flex items-center gap-1.5 h-8 px-3 rounded-xl text-xs font-semibold transition-all"
                          style={{
                            background: active ? catCfg.color : "var(--surface-1)",
                            border: `1px solid ${active ? catCfg.color : "var(--border-color)"}`,
                            color: active ? "#fff" : "var(--text-secondary)",
                          }}>
                          <CatIcon className="w-3 h-3" />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {filtered.length === 0 ? (
                  <div className="rounded-2xl border p-10 text-center" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
                    <p className="text-sm" style={{ color:"var(--text-secondary)" }}>{t("Shop.no_services_available", lang)}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map((product, idx) => (
                      <ProductCard key={product.product_id} product={product} platform={platform}
                        lang={lang} user={user} login={login} addToCart={addToCart} idx={idx} exchangeRate={exchangeRate} />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

      </div>
    </div>
  );
}

// ── Featured Showcase ─────────────────────────────────────────────────────────
function PopularShowcase({ products, lang, user, login, addToCart, exchangeRate }) {
  const [modalOpen, setModalOpen] = useState(false);

  const bucket = Math.floor(Date.now() / (10 * 60 * 1000));

  const featured = useMemo(() => {
    const eligible = products.filter(p => {
      const cat = String(p.category || "").toLowerCase();
      return cat === "followers" || cat === "likes" || cat === "views";
    });
    if (!eligible.length) return null;
    const seed = hashSeed("featured_" + bucket);
    return eligible[Math.floor(seed * eligible.length)];
  }, [products, bucket]);

  if (!featured) return null;

  const cfg = CATEGORY_CONFIG[featured.category] || DEFAULT_CONFIG;
  const CategoryIcon = cfg.icon;
  const plat = getProductPlatform(featured);
  const platKey = plat === "__none__" ? "instagram" : plat;
  const platformCfg = PLATFORM_CONFIG[platKey];
  const PlatformIcon = platformCfg?.icon;
  const brand = platformCfg?.color || cfg.color;

  const minQuantity = featured.min_quantity || 10;
  const minPrice = calculateIpointsPrice(minQuantity, featured.ipoints_per_1000);
  const name = featured.name?.[lang] || featured.name?.en || "Product";

  return (
    <>
      <button onClick={() => setModalOpen(true)}
        className="popular-card-enter relative block w-full rounded-3xl overflow-hidden text-start mb-6 transition-transform hover:-translate-y-0.5 active:scale-[0.995]"
        style={{
          background: `linear-gradient(115deg, ${brand} 0%, ${brand}CC 50%, ${brand}66 100%)`,
          boxShadow: `0 12px 40px ${brand}44`,
        }}>

        {/* Platform icon watermark */}
        {PlatformIcon && (
          <div className="absolute -end-6 -bottom-8 opacity-[0.15] pointer-events-none"
            style={{ color: "#fff" }}>
            <PlatformIcon size={160} />
          </div>
        )}

        {/* Dark gradient for legibility */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 60%)" }} />

        <div className="relative z-10 flex items-center gap-4 p-5 sm:p-6 min-h-[148px]">
          <div className="flex-1 min-w-0">
            {/* Featured label */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full"
                style={{ background: "rgba(255,255,255,0.95)", color: brand }}>
                {L(lang, "Featured", "Рекомендуем", "מומלץ")}
              </span>
            </div>

            {/* Category line */}
            <div className="flex items-center gap-1.5 mb-1.5">
              <CategoryIcon className="w-3.5 h-3.5" style={{ color: "rgba(255,255,255,0.9)" }} />
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.9)" }}>
                {platformCfg?.label} · {getCategoryLabel(featured.category, lang)}
              </span>
            </div>

            {/* Name */}
            <h3 className="text-xl sm:text-2xl font-black leading-tight line-clamp-2 mb-3"
              style={{ color: "#fff", textShadow: "0 2px 10px rgba(0,0,0,0.25)" }}>
              {name}
            </h3>

            {/* Price + CTA */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-2xl font-black font-outfit leading-none" style={{ color: "#fff" }}>
                  {fmtPrice(minPrice)}
                </span>
                <span className="text-sm font-bold" style={{ color: "rgba(255,255,255,0.9)" }}>iP</span>
                {exchangeRate > 0 && (
                  <span className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.65)" }}>
                    ≈ ₪{(minPrice / exchangeRate).toFixed(2)}
                  </span>
                )}
              </div>
              <div className="h-10 px-4 rounded-full flex items-center gap-1.5 text-sm font-bold"
                style={{ background: "#fff", color: brand, boxShadow: "0 4px 14px rgba(0,0,0,0.2)" }}>
                {L(lang, "View", "Смотреть", "לצפות")}
                <ArrowRight className="w-4 h-4 rtl-flip" />
              </div>
            </div>
          </div>
        </div>
      </button>

      {modalOpen && (
        <ProductModal
          product={featured} brand={brand} lang={lang} user={user} login={login} addToCart={addToCart}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}

// ── Product helpers ───────────────────────────────────────────────────────────
const hashSeed = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return ((h >>> 0) % 10000) / 10000;
};

// ── Product Card (display — opens modal) ─────────────────────────────────────
function ProductCard({ product, platform, lang, user, login, addToCart, idx, exchangeRate }) {
  const [modalOpen, setModalOpen] = useState(false);
  const cfg = CATEGORY_CONFIG[product.category] || DEFAULT_CONFIG;
  const CategoryIcon = cfg.icon;

  const plat = platform || getProductPlatform(product);
  const platKey = plat === "__none__" ? "instagram" : plat;
  const platformCfg = PLATFORM_CONFIG[platKey];
  const brand = platformCfg?.color || cfg.color;

  const minQuantity = product.min_quantity || 10;
  const minPrice = calculateIpointsPrice(minQuantity, product.ipoints_per_1000);
  const desc = product.description?.[lang] || product.description?.en || "";

  const isNew = (() => {
    if (!product.created_at) return false;
    const age = Date.now() - new Date(product.created_at).getTime();
    return age < 7 * 24 * 60 * 60 * 1000;
  })();

  return (
    <>
      <button
        onClick={() => setModalOpen(true)}
        className="product-card-enter relative flex flex-col rounded-2xl overflow-hidden text-start w-full transition-all hover:-translate-y-1 active:scale-[0.98]"
        style={{
          background:`linear-gradient(135deg,${brand} 0%,${brand}CC 55%,${brand}88 100%)`,
          boxShadow: `0 6px 20px ${brand}33`,
          animationDelay:`${Math.min(idx*50,400)}ms`,
        }}
        data-testid={`product-${product.product_id}`}
      >
        {/* Legibility overlay */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background:"linear-gradient(180deg,rgba(0,0,0,0.08) 0%,rgba(0,0,0,0.35) 100%)" }} />

        {/* New badge — only for recently added products */}
        {isNew && (
          <div className="absolute top-3 end-3 z-10">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background:"rgba(255,255,255,0.95)", color: brand }}>
              {L(lang, "New", "Новинка", "חדש")}
            </span>
          </div>
        )}

        <div className="relative z-10 p-4 flex flex-col flex-1 gap-2.5">
          {/* Category label */}
          <div className="flex items-center gap-1.5">
            <CategoryIcon style={{ width:13, height:13, color:"rgba(255,255,255,0.85)" }} />
            <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color:"rgba(255,255,255,0.85)" }}>
              {getCategoryLabel(product.category, lang)}
            </span>
          </div>

          {/* Name */}
          <div className="font-black text-sm leading-snug" style={{ color:"#fff", textShadow:"0 1px 6px rgba(0,0,0,0.25)" }}>
            {product.name?.[lang] || product.name?.en || "Product"}
          </div>

          {/* Price + CTA */}
          <div className="mt-auto flex items-end justify-between pt-2.5 border-t" style={{ borderColor:"rgba(255,255,255,0.2)" }}>
            <div>
              <div className="text-[10px] mb-0.5" style={{ color:"rgba(255,255,255,0.65)" }}>
                {t("Shop.from", lang)}
              </div>
              <div className="text-xl font-black font-outfit leading-none" style={{ color:"#fff" }}>
                {fmtPrice(minPrice)} <span className="text-xs font-normal" style={{ opacity:0.8 }}>iP</span>
              </div>
              {exchangeRate > 0 && (
                <div className="text-[10px] mt-0.5" style={{ color:"rgba(255,255,255,0.6)" }}>
                  ≈ ₪{(minPrice / exchangeRate).toFixed(2)}
                </div>
              )}
            </div>
            <div className="h-9 px-3 rounded-xl flex items-center gap-1.5 text-xs font-bold"
              style={{ background:"rgba(255,255,255,0.95)", color:brand, boxShadow:"0 3px 12px rgba(0,0,0,0.25)" }}>
              {t("Shop.order", lang)}
              <ArrowRight className="w-3 h-3 rtl-flip" />
            </div>
          </div>
        </div>
      </button>

      {modalOpen && (
        <ProductModal
          product={product} brand={brand} lang={lang} user={user} login={login} addToCart={addToCart}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}

// ── Product Modal ─────────────────────────────────────────────────────────────
function ProductModal({ product, brand, lang, user, login, addToCart, onClose }) {
  const navigate = useNavigate();
  const minQuantity = product.min_quantity || 10;
  const maxQuantity = product.max_quantity || 200000;
  const [quantity, setQuantity]           = useState(minQuantity);
  const [quantityInput, setQuantityInput] = useState(String(minQuantity));
  const [link, setLink]                   = useState("");
  const [linkTouched, setLinkTouched]     = useState(false);
  const [customComments, setCustomComments]   = useState("");
  const [commentsTouched, setCommentsTouched] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded]   = useState(false);
  const [selectedEmoji, setSelectedEmoji] = useState(() => product.emoji_options?.[0] || null);
  const nudgeIntervalRef = useRef(null);
  const nudgeTimeoutRef  = useRef(null);

  const cfg = CATEGORY_CONFIG[product.category] || DEFAULT_CONFIG;
  const CategoryIcon = cfg.icon;
  const accent = brand || cfg.color;
  const targetMeta = getTargetFieldMeta(product, lang);
  const normalizedTarget = normalizeTargetInput(link, product);
  const linkError    = linkTouched ? validateTargetInput(link, product, lang) : null;
  const showPositive = Boolean(linkTouched && normalizedTarget && !linkError);
  const showNegative = Boolean(linkError);
  const normalizedComments = String(customComments || "").split("\n").map(l => l.trim()).filter(Boolean).join("\n");
  const commentsError = product.requires_custom_comments && commentsTouched && !normalizedComments
    ? t("Shop.enter_at_least_one_comment", lang)
    : null;
  const userBalance  = user?.ipoints || 0;
  const ipointsPrice = calculateIpointsPrice(quantity, product.ipoints_per_1000);
  const canAfford    = userBalance >= ipointsPrice;
  const quantityStep = getQuantityStep(product);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => { setQuantityInput(String(quantity)); }, [quantity]);

  const applyQuantityInput = () => {
    const n = clampQuantity(quantityInput, minQuantity, maxQuantity);
    setQuantity(n); setQuantityInput(String(n));
  };
  const nudge = (dir) => setQuantity(prev => clampQuantity(prev + dir * quantityStep, minQuantity, maxQuantity));
  const startNudge = (dir) => {
    nudge(dir);
    nudgeTimeoutRef.current = setTimeout(() => {
      nudgeIntervalRef.current = setInterval(() => {
        setQuantity(prev => clampQuantity(prev + dir * quantityStep, minQuantity, maxQuantity));
      }, 80);
    }, 400);
  };
  const stopNudge = () => { clearTimeout(nudgeTimeoutRef.current); clearInterval(nudgeIntervalRef.current); };

  const handleAdd = async () => {
    if (!user) { login(); return; }
    setLinkTouched(true); setCommentsTouched(true);
    const err = validateTargetInput(link, product, lang);
    if (err) { toast.error(err); return; }
    if (commentsError) { toast.error(commentsError); return; }
    if (product.emoji_options?.length > 0 && !selectedEmoji) {
      toast.error(L(lang, "Please select a reaction", "Выберите реакцию", "אנא בחר תגובה"));
      return;
    }
    if (!canAfford) {
      const deficit = Math.round((ipointsPrice - userBalance) * 100) / 100;
      sessionStorage.setItem("shop_resume", JSON.stringify({
        product_id: product.product_id,
        quantity,
        link: normalizedTarget || link,
      }));
      navigate(`/topup?needed_ip=${deficit}`);
      onClose();
      return;
    }
    setAdding(true);
    try {
      await addToCart(product.product_id, quantity, normalizedTarget, null, null, {
        target_url: normalizedTarget,
        custom_comments: product.requires_custom_comments ? normalizedComments : undefined,
        selected_emoji_service_id: selectedEmoji?.service_id || undefined,
        selected_emoji: selectedEmoji?.emoji || undefined,
      });
      toast.success(t("shop.added", lang));
      setAdded(true);
      setTimeout(() => { setAdded(false); onClose(); }, 1500);
    } catch (err) { toast.error(err.message); }
    finally { setAdding(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background:"rgba(0,0,0,0.65)" }}
      role="dialog"
      aria-modal="true"
      aria-label={product.name?.[lang] || product.name?.en || "Product"}
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl overflow-hidden flex flex-col"
        style={{ background:"var(--bg-secondary)", maxHeight:"92vh" }}>

        {/* Gradient header with watermark */}
        <div className="relative overflow-hidden px-5 py-5 flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}BB 100%)` }}>
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.35) 100%)" }} />
          <div className="absolute -end-6 -top-4 opacity-[0.15] pointer-events-none" style={{ color: "#fff" }}>
            <CategoryIcon style={{ width: 110, height: 110 }} />
          </div>
          <div className="relative z-10 flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base leading-snug mb-2"
                style={{ color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,0.2)" }}>
                {product.name?.[lang] || product.name?.en || "Product"}
              </div>
            </div>
            <button onClick={onClose}
              className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:opacity-80 flex-shrink-0"
              style={{ background: "rgba(255,255,255,0.2)", color: "#fff" }}
              aria-label={lang === "he" ? "סגור" : lang === "ru" ? "Закрыть" : "Close"}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {(product.description?.[lang] || product.description?.en) && (
            <p className="text-sm leading-relaxed" style={{ color:"var(--text-secondary)" }}>
              {product.description?.[lang] || product.description?.en}
            </p>
          )}

          {/* Quantity */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold" style={{ color:"var(--text-secondary)" }}>
                {t("shop.quantity", lang)}
              </span>
              <span className="text-sm font-black font-outfit" style={{ color:"var(--text-primary)" }}>
                {quantity.toLocaleString()}
              </span>
            </div>
            <Slider value={[quantity]} onValueChange={(val) => { setQuantity(val[0]); setQuantityInput(String(val[0])); }}
              min={minQuantity} max={maxQuantity} step={quantityStep} className="mb-3"
              dir={lang === "he" ? "rtl" : "ltr"} />
            <div className="flex items-center gap-2" dir="ltr">
              <button onMouseDown={() => startNudge(-1)} onMouseUp={stopNudge} onMouseLeave={stopNudge}
                onTouchStart={(e) => { e.preventDefault(); startNudge(-1); }} onTouchEnd={stopNudge}
                className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:opacity-80 select-none"
                style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)", color:"var(--text-secondary)" }}
                aria-label={lang === "he" ? "הפחת כמות" : lang === "ru" ? "Уменьшить количество" : "Decrease quantity"}>
                <Minus className="w-3.5 h-3.5" />
              </button>
              <Input type="text" inputMode="numeric" value={quantityInput}
                onChange={(e) => setQuantityInput(e.target.value.replace(/[^\d]/g,""))}
                onBlur={applyQuantityInput} onKeyDown={(e) => { if (e.key==="Enter") applyQuantityInput(); }}
                className="h-9 rounded-xl text-sm font-outfit text-center flex-1 font-bold" dir="ltr"
                style={{ border: `1.5px solid ${accent}55`, background: "var(--surface-1)", boxShadow: `0 0 0 3px ${accent}12` }} />
              <button onMouseDown={() => startNudge(1)} onMouseUp={stopNudge} onMouseLeave={stopNudge}
                onTouchStart={(e) => { e.preventDefault(); startNudge(1); }} onTouchEnd={stopNudge}
                className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:opacity-80 select-none"
                style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)", color:"var(--text-secondary)" }}
                aria-label={lang === "he" ? "הגדל כמות" : lang === "ru" ? "Увеличить количество" : "Increase quantity"}>
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex justify-between text-[10px] mt-1.5 px-0.5" style={{ color:"var(--text-secondary)", opacity:0.5 }}>
              <span>min {minQuantity.toLocaleString()}</span>
              <span>max {maxQuantity.toLocaleString()}</span>
            </div>
          </div>

          {/* Emoji Reaction Selector */}
          {product.emoji_options?.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-xs font-semibold" style={{ color:"var(--text-secondary)" }}>
                  {L(lang, "Choose reaction", "Выберите реакцию", "בחר תגובה")}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.emoji_options.map((opt, i) => (
                  <button key={i} type="button"
                    onClick={() => setSelectedEmoji(opt)}
                    className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl border transition-all"
                    style={selectedEmoji?.emoji === opt.emoji
                      ? { background:`${accent}18`, borderColor:`${accent}55`, boxShadow:`0 0 0 2px ${accent}30` }
                      : { background:"var(--surface-2)", borderColor:"var(--border-color)" }}>
                    <span className="text-2xl leading-none">{opt.emoji}</span>
                    {opt.label && <span className="text-[10px] font-semibold" style={{ color: selectedEmoji?.emoji === opt.emoji ? accent : "var(--text-secondary)" }}>{opt.label}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Link */}
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <LinkIcon className="w-3.5 h-3.5" style={{ color:"var(--text-secondary)" }} />
              <span className="text-xs font-semibold" style={{ color:"var(--text-secondary)" }}>{targetMeta.label}</span>
            </div>
            <div className="relative">
              <Input placeholder={targetMeta.placeholder} value={link}
                onChange={(e) => { setLink(e.target.value); if (!linkTouched) setLinkTouched(true); }}
                onBlur={() => { setLinkTouched(true); const n = normalizeTargetInput(link, product); if (n) setLink(n); }}
                className="text-sm h-10 rounded-xl pe-8"
                style={{
                  border: `1.5px solid ${showNegative ? "#ef4444" : showPositive ? "#22c55e" : `${accent}55`}`,
                  background: "var(--surface-1)",
                  color: "var(--text-primary)",
                  boxShadow: showNegative ? "0 0 0 3px rgba(239,68,68,0.1)" : showPositive ? "0 0 0 3px rgba(34,197,94,0.1)" : `0 0 0 3px ${accent}12`,
                }}
                dir="ltr" data-testid={`instagram-link-${product.product_id}`} />
              {link.trim() && (showPositive || showNegative) && (
                <span className={`absolute end-3 top-1/2 -translate-y-1/2 text-sm font-black ${showPositive ? "text-green-400" : "text-red-400"}`}>
                  {showPositive ? "✓" : "✗"}
                </span>
              )}
            </div>
            {linkError
              ? <p className="text-xs mt-1 text-red-400">{linkError}</p>
              : <p className="text-xs mt-1 opacity-40" style={{ color:"var(--text-secondary)" }}>{targetMeta.hint}</p>}
            {!showPositive && targetMeta.examples?.length > 0 && (
              <div className="mt-2 rounded-xl p-2.5" style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)" }}>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color:"var(--text-secondary)" }}>
                  {{"en":"Valid formats","ru":"Допустимые форматы","he":"פורמטים נכונים"}[lang] || "Valid formats"}
                </p>
                <ul className="space-y-1">
                  {targetMeta.examples.map((ex, i) => (
                    <li key={i} className="flex items-center gap-2 text-[11px] font-mono leading-snug" style={{ color:"var(--text-primary)" }}>
                      <span style={{ color:"#22c55e", flexShrink:0 }}>•</span>
                      <span className="break-all opacity-80">{ex}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Custom Comments */}
          {product.requires_custom_comments && (() => {
            const commentLines = normalizedComments ? normalizedComments.split("\n").filter(Boolean) : [];
            const targetCount = Math.max(10, quantity);
            const lineCount = commentLines.length;
            const countColor = lineCount === 0 ? "var(--text-muted)" : lineCount >= targetCount ? "#22c55e" : "#f59e0b";
            return (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5" style={{ color:"var(--text-secondary)" }} />
                  <span className="text-xs font-semibold" style={{ color:"var(--text-secondary)" }}>
                    {t("Shop.custom_comments", lang)}
                  </span>
                </div>
                {lineCount > 0 && (
                  <span className="text-xs font-semibold tabular-nums" style={{ color: countColor }}>
                    {t("Shop.comments_count", lang).replace("{count}", lineCount).replace("{target}", targetCount)}
                  </span>
                )}
              </div>
              <Textarea
                placeholder={t("Shop.one_comment_per_line", lang)}
                value={customComments}
                onChange={(e) => { setCustomComments(e.target.value); if (!commentsTouched) setCommentsTouched(true); }}
                onBlur={() => setCommentsTouched(true)}
                className="min-h-[100px] rounded-xl text-sm"
                style={{
                  border: `1.5px solid ${commentsError ? "#ef4444" : lineCount >= targetCount ? "#22c55e" : lineCount > 0 ? "#f59e0b" : `${accent}55`}`,
                  background: "var(--surface-1)",
                  color: "var(--text-primary)",
                  boxShadow: commentsError ? "0 0 0 3px rgba(239,68,68,0.1)" : lineCount >= targetCount ? "0 0 0 3px rgba(34,197,94,0.1)" : lineCount > 0 ? "0 0 0 3px rgba(245,158,11,0.1)" : `0 0 0 3px ${accent}12`,
                }} />
              <p className="text-xs mt-1.5 leading-relaxed" style={{ color: "var(--text-muted)" }}>
                {t("Shop.comments_hint", lang).replace("{n}", targetCount)}
              </p>
              {commentsError && <p className="text-xs mt-1 text-red-400">{commentsError}</p>}
            </div>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="border-t px-5 py-4 flex-shrink-0" style={{ borderColor:"var(--border-color)" }}>
          {/* Price card */}
          <div className="flex items-center justify-between rounded-xl px-3.5 py-2.5 mb-3"
            style={{
              background: `${accent}10`,
              border: `1px solid ${accent}28`,
            }}>
            <span className="text-xs font-semibold" style={{ color:"var(--text-secondary)" }}>{t("Shop.total", lang)}</span>
            <span className="text-2xl font-black font-outfit leading-none flex items-center gap-1.5" style={{ color: accent }}>
              <Coins className="w-5 h-5" />
              {fmtPrice(ipointsPrice)} <span className="text-sm font-normal opacity-75">iP</span>
            </span>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="h-11 px-4 rounded-xl font-semibold text-sm flex-shrink-0 transition-all hover:opacity-80"
              style={{ background:"var(--surface-2)", color:"var(--text-secondary)", border:"1px solid var(--border-color)" }}>
              {L(lang, "Cancel", "Отмена", "ביטול")}
            </button>
            {user && !canAfford ? (
              <a href={`/topup?needed_ip=${Math.round((ipointsPrice - userBalance) * 100) / 100}`}
                onClick={(e) => {
                  e.stopPropagation();
                  sessionStorage.setItem("shop_resume", JSON.stringify({
                    product_id: product.product_id,
                    quantity,
                    link: normalizedTarget || link,
                  }));
                  onClose();
                }}
                className="flex-1 h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90"
                style={{ background:"linear-gradient(135deg,#E1306C,#833AB4)", color:"#fff", boxShadow:"0 4px 16px rgba(225,48,108,0.4)", textDecoration:"none" }}>
                <Coins className="w-4 h-4" />
                {L(lang,
                  `Top up: ${fmtPrice(ipointsPrice-userBalance)} iP needed`,
                  `Пополнить: нужно ${fmtPrice(ipointsPrice-userBalance)} iP`,
                  `לטעון: חסרים ${fmtPrice(ipointsPrice-userBalance)} iP`
                )}
              </a>
            ) : (
              <button onClick={handleAdd} disabled={adding || added}
                className="flex-1 h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-60"
                style={added
                  ? { background:"rgba(34,197,94,0.12)", color:"#22c55e", border:"1px solid rgba(34,197,94,0.25)" }
                  : { background: adding ? `${accent}90` : `linear-gradient(135deg, ${accent}, ${accent}CC)`, color:"#fff", boxShadow: adding ? "none" : `0 4px 16px ${accent}55` }}
                data-testid={`add-to-cart-${product.product_id}`}>
                {added   ? <><Check className="w-4 h-4" /> {t("shop.added", lang)}</> :
                 adding  ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> {t("common.loading", lang)}</> :
                           <><ShoppingCart className="w-4 h-4" /> {t("shop.add_to_cart", lang)}</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
